import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeLatest, takeEvery, getContext, put, call} from 'redux-saga/effects'
import Api, {ApiError, UnauthorizedError} from 'Api'
import {AlertMessageData} from '../../components/AlertMessage'
import {CsvImportFailureSummary, CsvImportSuccessSummary} from 'models/CsvImportSummary'
import {CsvValidationSummary} from 'models/CsvValidationSummary'
import {actions as searchActions} from './search'

export type ImportCsvState = {
  uploading: boolean
  error?: AlertMessageData
  successSummary?: CsvImportSuccessSummary
  failureSummary?: CsvImportFailureSummary
  // True whenever an actual upload request to the backend failed (structural CSV validation,
  // per-row failures, duplicate-file conflict, or any other server error) — as opposed to
  // client-side errors (wrong file type, unreadable file) where no request was ever made.
  // The endpoint never makes partial writes, so this is always "no changes were made".
  uploadFailed: boolean
  validating: boolean
  validationResult?: CsvValidationSummary
  validationError?: AlertMessageData
}

export const initialState: ImportCsvState = {
  uploading: false,
  error: undefined,
  successSummary: undefined,
  failureSummary: undefined,
  uploadFailed: false,
  validating: false,
  validationResult: undefined,
  validationError: undefined,
}

export const actions = {
  upload: createAction<{file: File}>('importCsv/upload'),
  reset: createAction<void>('importCsv/reset'),
  setUploading: createAction<{uploading: boolean}>('importCsv/setUploading'),
  setError: createAction<{error?: AlertMessageData}>('importCsv/setError'),
  setSuccessSummary: createAction<{summary?: CsvImportSuccessSummary}>(
    'importCsv/setSuccessSummary',
  ),
  setFailureSummary: createAction<{summary?: CsvImportFailureSummary}>(
    'importCsv/setFailureSummary',
  ),
  setUploadFailed: createAction<{failed: boolean}>('importCsv/setUploadFailed'),
  validate: createAction<{file: File}>('importCsv/validate'),
  setValidating: createAction<{validating: boolean}>('importCsv/setValidating'),
  setValidationResult: createAction<{result?: CsvValidationSummary}>(
    'importCsv/setValidationResult',
  ),
  setValidationError: createAction<{error?: AlertMessageData}>('importCsv/setValidationError'),
}

export const reducer = createReducer(initialState, builder => {
  builder
    .addCase(actions.reset, () => ({...initialState}))
    .addCase(actions.setUploading, (state, action: ReturnType<typeof actions.setUploading>) => {
      state.uploading = action.payload.uploading
    })
    .addCase(actions.setError, (state, action: ReturnType<typeof actions.setError>) => {
      state.error = action.payload.error as AlertMessageData | undefined
    })
    .addCase(
      actions.setSuccessSummary,
      (state, action: ReturnType<typeof actions.setSuccessSummary>) => {
        state.successSummary = action.payload.summary
      },
    )
    .addCase(
      actions.setFailureSummary,
      (state, action: ReturnType<typeof actions.setFailureSummary>) => {
        state.failureSummary = action.payload.summary
      },
    )
    .addCase(
      actions.setUploadFailed,
      (state, action: ReturnType<typeof actions.setUploadFailed>) => {
        state.uploadFailed = action.payload.failed
      },
    )
    .addCase(actions.setValidating, (state, action: ReturnType<typeof actions.setValidating>) => {
      state.validating = action.payload.validating
    })
    .addCase(
      actions.setValidationResult,
      (state, action: ReturnType<typeof actions.setValidationResult>) => {
        state.validationResult = action.payload.result
      },
    )
    .addCase(
      actions.setValidationError,
      (state, action: ReturnType<typeof actions.setValidationError>) => {
        state.validationError = action.payload.error as AlertMessageData | undefined
      },
    )
})

const uploadSaga = function*(action: ReturnType<typeof actions.upload>) {
  const {file} = action.payload
  const api: Api = yield getContext('api')

  yield put(actions.setUploading({uploading: true}))
  yield put(actions.setError({error: undefined}))
  yield put(actions.setSuccessSummary({summary: undefined}))
  yield put(actions.setFailureSummary({summary: undefined}))
  yield put(actions.setUploadFailed({failed: false}))

  try {
    const summary: CsvImportSuccessSummary = yield call(api.uploadCsv, file)
    yield put(actions.setSuccessSummary({summary}))
    // Devices/patients the CSV touched (e.g. a replaced device's status) live in the search
    // slice's grid data, not this wizard's own state — keep it from going stale after upload.
    yield put(searchActions.refreshSearch())
  } catch (error) {
    if (error === UnauthorizedError) {
      throw error
    }
    const message =
      error instanceof ApiError ? error.message : 'Unknown error occurred during CSV upload.'
    const details = error instanceof ApiError ? error.details ?? [] : []
    yield put(actions.setError({error: {message, details}}))
    yield put(actions.setUploadFailed({failed: true}))
    if (error instanceof ApiError && error.batchSummary) {
      yield put(actions.setFailureSummary({summary: error.batchSummary}))
    }
  } finally {
    yield put(actions.setUploading({uploading: false}))
  }
}

// Bumped whenever a validate() completes or a reset() happens (e.g. "Fix & Re-upload" while a
// validation is in flight) — lets a still-pending validateSaga tell its response is stale and
// skip writing into a store the user has already navigated away from.
let latestValidateRequestId = 0

const isAbortError = (error: unknown) => (error as Error)?.name === 'AbortError'

const validateSaga = function*(action: ReturnType<typeof actions.validate>) {
  const requestId = ++latestValidateRequestId
  const {file} = action.payload
  const api: Api = yield getContext('api')

  yield put(actions.setValidating({validating: true}))
  yield put(actions.setValidationError({error: undefined}))
  yield put(actions.setValidationResult({result: undefined}))

  try {
    const result: CsvValidationSummary = yield call(api.validateCsv, file)
    if (requestId !== latestValidateRequestId) {
      return
    }
    yield put(actions.setValidationResult({result}))
  } catch (error) {
    if (error === UnauthorizedError) {
      throw error
    }
    if (isAbortError(error) || requestId !== latestValidateRequestId) {
      return
    }
    const message =
      error instanceof ApiError ? error.message : 'Unknown error occurred during CSV validation.'
    const details = error instanceof ApiError ? error.details ?? [] : []
    yield put(actions.setValidationError({error: {message, details}}))
  } finally {
    if (requestId === latestValidateRequestId) {
      yield put(actions.setValidating({validating: false}))
    }
  }
}

export const saga = function*() {
  yield takeLatest(actions.upload, uploadSaga)
  yield takeLatest(actions.validate, validateSaga)
  yield takeEvery(actions.reset, function*() {
    latestValidateRequestId++
  })
}
